import React, { useEffect, useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
// The reads, the date formats and the progress rules are shared with the phone Progress screen
// (features/progress/progressData.js, docs/mobile-pwa-plan.md "One copy of every action").
import {
  formatDateCentral, formatDateTimeCentralShort, getProductionProgress, loadTodayLists, searchParts, loadPartProgress,
} from '../features/progress/progressData';

export default function ProgressPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // A) finishing, ready to ship, shipped
  const [finishingToday, setFinishingToday] = useState([]);
  const [readyToShip, setReadyToShip] = useState([]);
  const [shippedToday, setShippedToday] = useState([]);

  // B) part search
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchFocused, setSearchFocused] = useState(false);

  // C) all open jobs for the selected part
  const [allJobsProgress, setAllJobsProgress] = useState([]);

  const wrapperRef = useRef(null);
  const debounceRef = useRef(null); // For debouncing searches

  useEffect(() => {
    fetchFinishingAndShippingToday();
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  async function fetchFinishingAndShippingToday() {
    setLoading(true);
    setError("");
    try {
      const { finishing, shipped, ready } = await loadTodayLists();
      setFinishingToday(finishing);
      setShippedToday(shipped);
      setReadyToShip(ready);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching finishing/shipping data:", err);
      setError(err.message);
      setLoading(false);
    }
  }

  // ==============================================
  // 2) "Where Is This Part?" search
  // Debounce the search to avoid rapid firing
  // ==============================================
  function handleSearchChange(e) {
    const val = e.target.value;
    setSearchText(val);
    setAllJobsProgress([]);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce so we only search after user stops typing for ~300ms
    debounceRef.current = setTimeout(() => {
      executeSearch(val);
    }, 300);
  }

  async function executeSearch(query) {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const data = await searchParts(query);
    setSearchResults(data);
    if (data.length) setSearchFocused(true);
  }

  function handleSelectSearchItem(partObj) {
    setSearchText(`${partObj.product_id} - ${partObj.description}`);
    setSearchResults([]);
    setSearchFocused(false);
    fetchPartProgress(partObj.part_id);
  }

  // The part's open jobs with their stations, furthest along first (features/progress loadPartProgress).
  async function fetchPartProgress(partId) {
    setLoading(true);
    setError("");
    setAllJobsProgress([]);
    try {
      setAllJobsProgress(await loadPartProgress(partId));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching part progress:", err);
      setError(err.message);
      setLoading(false);
    }
  }

  function handleClickOutside(e) {
    if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
      setSearchFocused(false);
    }
  }

  // ==============================================
  // RENDER
  // ==============================================
  return (
    <div className="container my-4">
      {loading && (
        <div className="text-secondary">Loading Progress Data...</div>
      )}
      {error && <div className="alert alert-danger">Error: {error}</div>}

      {!loading && !error && (
        <>
          <h2 className="mb-4 text-center">Progress Overview</h2>

          {/* SECTION A: Where Is This Part? */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">
                <i className="bi bi-search me-2"></i>Where Is This Part Right
                Now?
              </h5>
              <div
                ref={wrapperRef}
                style={{ position: "relative", maxWidth: "400px" }}
                className="mb-2"
              >
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search part by ID or description..."
                  value={searchText}
                  onChange={handleSearchChange}
                  onFocus={() => setSearchFocused(true)}
                />
                {searchFocused && searchResults.length > 0 && (
                  <ul
                    className="list-group"
                    style={{
                      position: "absolute",
                      top: "100%",
                      width: "100%",
                      zIndex: 999,
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    {searchResults.map((p) => (
                      <li
                        key={p.part_id}
                        className="list-group-item list-group-item-action"
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSelectSearchItem(p)}
                      >
                        <strong>{p.product_id}</strong> - {p.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {allJobsProgress.length > 0 ? (
                <div className="mt-3">
                  <h6>Open Jobs for this Part:</h6>
                  {allJobsProgress.map((job) => (
                    <div key={job.job_id} className="mb-4">
                      <PartProgressDisplay data={job} />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-3">
                  <h6>Open Jobs for this Part:</h6>
                  <p className="text-muted">
                    🛑 No pending or in-progress jobs were found for this part.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* SECTION B: Shipped Today */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-check2-circle me-2"></i>Shipped Today
              </h5>
              {shippedToday.length === 0 ? (
                <p className="text-muted">No parts shipped today.</p>
              ) : (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Part</th>
                      <th>Description</th>
                      <th>Qty Shipped</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shippedToday.map((line) => (
                      <tr key={line.task_id}>
                        <td>{line.master_job_num}</td>
                        <td>{line.product_id}</td>
                        <td>{line.description}</td>
                        <td>{line.quantity_shipped}</td>
                        <td>{formatDateCentral(line.shipped_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* SECTION C: Ready to Ship */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-truck me-2"></i>Ready to Ship
              </h5>
              {readyToShip.length === 0 ? (
                <p className="text-muted">No parts ready to ship today.</p>
              ) : (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Part</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Scheduled</th>
                    </tr>
                  </thead>
                  <tbody>
                    {readyToShip.map((line) => (
                      <tr key={line.task_id}>
                        <td>{line.master_job_num}</td>
                        <td>{line.product_id}</td>
                        <td>{line.description}</td>
                        <td>
                          {line.quantity_ordered} / {line.quantity_shipped || 0}
                        </td>
                        <td>
                          {line.scheduled_date
                            ? formatDateCentral(line.scheduled_date)
                            : "Today"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* SECTION D: Finishing Today */}
          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <h5 className="card-title">
                <i className="bi bi-palette-fill me-2"></i>Finishing Today
              </h5>
              {finishingToday.length === 0 ? (
                <p className="text-muted">No parts scheduled to finish today.</p>
              ) : (
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Job</th>
                      <th>Part</th>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {finishingToday.map((item) => {
                      const badgeClass = /in\s*progress/i.test(
                        item.status || ""
                      )
                        ? "badge bg-warning text-dark"
                        : "badge bg-info";
                      return (
                        <tr key={item.task_id}>
                          <td>{item.master_job_num}</td>
                          <td>{item.product_id}</td>
                          <td>{item.description}</td>
                          <td>
                            {item.quantity_ordered} /{" "}
                            {item.quantity_shipped || 0}
                          </td>
                          <td>
                            <span className={badgeClass}>
                              {item.status || "Finishing"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Subcomponent that displays job + station data
function PartProgressDisplay({ data }) {
  // Single source of truth for progress — shared with the list sorter above so
  // the visual progress bar always matches the ordering of the cards.
  const { stationsPresent, currentIndex } = getProductionProgress(data.stations);

  return (
    <div className="border rounded p-3">
      <h6 className="mb-1">
        Job #{data.master_job_num ?? data.job_id} — Part: {data.product_id}
      </h6>
      <p className="mb-1 text-muted">{data.description}</p>
      <p className="mb-1">
        <small>
          <strong>Ordered:</strong> {data.quantity_ordered} &nbsp;|&nbsp;
          <strong>Defected:</strong> {data.quantity_defected || 0} &nbsp;|&nbsp;
          <strong>Expected:</strong> {data.quantity_expected || 0}
        </small>
      </p>
      <p className="mb-2">
        <small>
          <strong>Estimated Completion:</strong>{" "}
          {data.estimated_completion_date
            ? formatDateCentral(data.estimated_completion_date)
            : "N/A"}
        </small>
      </p>

      <div className="mb-3">
        <div className="d-flex justify-content-between mb-2">
          {stationsPresent.map((st, idx) => {
            let circleClass = "bg-secondary";
            if (idx < currentIndex) circleClass = "bg-success";
            else if (idx === currentIndex) circleClass = "bg-info";
            return (
              <div key={st} className="text-center" style={{ flex: 1 }}>
                <div
                  className={`rounded-circle mx-auto mb-1 ${circleClass}`}
                  style={{ width: "20px", height: "20px", lineHeight: "20px" }}
                />
                <small style={{ whiteSpace: "nowrap" }}>{st}</small>
              </div>
            );
          })}
        </div>
        <div className="progress" style={{ height: "4px" }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{
              width: `${(currentIndex / stationsPresent.length) * 100}%`,
            }}
          />
        </div>
      </div>

      <table className="table table-sm table-bordered">
        <thead className="table-light">
          <tr>
            <th>Station</th>
            <th>Status</th>
            <th>Scheduled</th>
            <th>Start</th>
            <th>End</th>
          </tr>
        </thead>
        <tbody>
          {(data.stations || []).map((s) => (
            <tr key={s.station}>
              <td>{s.station}</td>
              <td>
                {s.status ? (
                  <span className="badge bg-primary">{s.status}</span>
                ) : (
                  <span className="badge bg-secondary">Pending</span>
                )}
              </td>
              <td>{formatDateCentral(s.scheduled_date)}</td>
              <td>{formatDateTimeCentralShort(s.start_time)}</td>
              <td>{formatDateTimeCentralShort(s.end_time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
